// src/features/Learner/components/ScheduleTable.js
import React, { useState, useMemo, useCallback } from 'react';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';

const LESSONS_PER_WEEK = 2;
const CONTENT_COMPLETE_THRESHOLD = 0.9; // matches LmsProgressReport.js
const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;
const WEEKS_SHOWN_AROUND_CURRENT = 1; // 1 week before + current + 1 week after, by default

// Cycles every 4 weeks, purely for visual rhythm — unrelated to the calendar.
const BAND_COLORS = ['transparent', '#eff6ff', '#fefce8', '#fdf2f8'];
const CURRENT_WEEK_ACCENT = '#19a4a0';

const LESSON_STATE_LEGEND = [
    { label: 'Not started', color: '#9ca3af', text: 'you haven\u2019t opened this lesson\u2019s content yet' },
    { label: 'In progress', color: '#9ca3af', text: 'a progress bar shows how much you\u2019ve completed' },
    { label: 'Ready for tutor', color: '#92400e', text: 'content\u2019s done \u2014 bring it up in your next session' },
    { label: 'Done \u2713', color: '#19a4a0', text: 'content finished and covered with your tutor' },
];

const toDateObj = (v) => (v?.toDate ? v.toDate() : new Date(v));

const formatShortDate = (date) =>
    date.toLocaleDateString('default', { day: 'numeric', month: 'short' });

const formatMonthLabel = (date) =>
    date.toLocaleDateString('default', { month: 'long', year: 'numeric' });

const toDateInputValue = (date) => date.toLocaleDateString('en-CA'); // YYYY-MM-DD, local time

// ---------------------------------------------------------------------------
// Per-lesson status. This intentionally never says "late" — only the overall
// pace banner carries that judgment: an individual lesson being unconfirmed
// by a tutor isn't a problem in itself, since tutor sessions are undated
// and flexible.
// ---------------------------------------------------------------------------
const getLessonStatus = (lesson, userProgress, tutorConfirmedLessonIds) => {
    const progress = userProgress[lesson.id] || { totalNavItems: 0, completedCount: 0 };
    const { totalNavItems, completedCount } = progress;
    const contentPct = totalNavItems > 0 ? Math.round((completedCount / totalNavItems) * 100) : 0;
    const contentComplete = totalNavItems > 0 && (completedCount / totalNavItems) >= CONTENT_COMPLETE_THRESHOLD;
    const tutorConfirmed = tutorConfirmedLessonIds.has(lesson.id);

    let state;
    if (contentComplete && tutorConfirmed) state = 'done';
    else if (contentComplete) state = 'ready-for-tutor';
    else if (contentPct > 0) state = 'in-progress';
    else state = 'not-started';

    return { state, contentPct, contentComplete, tutorConfirmed };
};

// ---------------------------------------------------------------------------
// LessonRow — one line per lesson, used in both collapsed (past week) and
// expanded (current/future week) contexts. When editable, the target date
// becomes a date input instead of plain text (individual learners only).
// ---------------------------------------------------------------------------
const LessonRow = ({ lesson, status, targetDate, showProgressBar, editable, onDateChange, isPast }) => {
    const { state, contentPct } = status;
    const dateWeight = isPast ? 400 : 600;

    const dateInput = editable && targetDate && (
        <input
            type="date"
            value={toDateInputValue(targetDate)}
            onChange={(e) => onDateChange(lesson.id, e.target.value)}
            onClick={(e) => e.stopPropagation()}
            style={{
                fontSize: '11px', color: '#6b7280', border: '1px solid #e5e7eb', fontWeight: dateWeight,
                borderRadius: '6px', padding: '2px 6px', backgroundColor: 'white',
            }}
        />
    );

    let rightContent;
    if (state === 'done') {
        rightContent = <span style={{ color: '#19a4a0', fontSize: '15px' }}>&#10003;</span>;
    } else if (state === 'ready-for-tutor') {
        rightContent = (
            <span style={{
                fontSize: '11px', padding: '2px 8px', borderRadius: '6px',
                backgroundColor: '#fef3c7', color: '#92400e',
            }}>Ready for tutor</span>
        );
    } else if (showProgressBar && state === 'in-progress' && !editable) {
        rightContent = (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '48px', height: '4px', backgroundColor: '#e5e7eb', borderRadius: '2px', overflow: 'hidden' }}>
                    <div style={{ width: `${contentPct}%`, height: '100%', backgroundColor: '#19a4a0' }} />
                </div>
                <span style={{ fontSize: '11px', color: '#6b7280', width: '28px', textAlign: 'right' }}>{contentPct}%</span>
            </div>
        );
    } else if (dateInput) {
        rightContent = dateInput;
    } else if (targetDate) {
        rightContent = <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: dateWeight }}>by {formatShortDate(targetDate)}</span>;
    } else {
        rightContent = <span style={{ fontSize: '11px', color: '#9ca3af' }}>Not started</span>;
    }

    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: '7px 10px', borderRadius: '8px',
            backgroundColor: state === 'not-started' ? 'transparent' : '#f9fafb',
        }}>
            <span style={{ fontSize: '13px', flex: 1, color: state === 'not-started' ? '#6b7280' : '#111827' }}>
                {lesson.title}
            </span>
            {editable && showProgressBar && state === 'in-progress' && (
                <span style={{ fontSize: '11px', color: '#6b7280' }}>{contentPct}%</span>
            )}
            {rightContent}
        </div>
    );
};

// ---------------------------------------------------------------------------
// ScheduleTable
//
// Funded/cohort learners: fixed 2-lessons/week plan from enrolledAt, pace
// judged against that plan — this is the version discussed originally.
//
// Individual learners (isIndividualLearner=true — cohort "Other" or none):
// same default dates to start, but each learner can drag their own target
// date per lesson to plan around their own pace. Their pace banner compares
// against their own dates instead of the fixed 2/week formula.
// ---------------------------------------------------------------------------
const ScheduleTable = ({
    lessons, userProgress, enrolledAt, tutorConfirmedLessonIds,
    isIndividualLearner = false, enrollmentId = null, initialCustomLessonDates = {},
}) => {
    const [expanded, setExpanded] = useState(false);
    const [customDates, setCustomDates] = useState(initialCustomLessonDates);
    const [saveError, setSaveError] = useState(null);
    const [legendOpen, setLegendOpen] = useState(false);

    // Shared with the pace calc below — kept in sync via the same formula so the
    // cascade always sees the same "effective date" the display is showing.
    const getDefaultTargetDate = useCallback((index) => {
        const enrolledDate = toDateObj(enrolledAt);
        const weekNumber = Math.ceil((index + 1) / LESSONS_PER_WEEK);
        return new Date(enrolledDate.getTime() + weekNumber * MS_PER_WEEK);
    }, [enrolledAt]);

    const getEffectiveTargetDate = useCallback((lesson, index, datesMap) => {
        const override = datesMap[lesson.id];
        return override ? toDateObj(override) : getDefaultTargetDate(index);
    }, [getDefaultTargetDate]);

    // Editing a lesson's date shifts every later lesson by the same offset, so the
    // rest of the plan stays in order relative to the one you just moved — rather
    // than leaving everything else pinned to its old date.
    const handleDateChange = useCallback(async (lessonId, dateStr) => {
        if (!dateStr || !enrollmentId) return;
        const editedIndex = lessons.findIndex((l) => l.id === lessonId);
        if (editedIndex === -1) return;

        const newDate = new Date(`${dateStr}T00:00:00`);
        const oldDate = getEffectiveTargetDate(lessons[editedIndex], editedIndex, customDates);
        const deltaMs = newDate.getTime() - oldDate.getTime();

        const previousDates = customDates; // for rollback if the write fails
        const updatedDates = { ...customDates };
        const firestoreUpdates = {};
        for (let i = editedIndex; i < lessons.length; i += 1) {
            const lesson = lessons[i];
            const shiftedDate = i === editedIndex
                ? newDate
                : new Date(getEffectiveTargetDate(lesson, i, customDates).getTime() + deltaMs);
            const ts = Timestamp.fromDate(shiftedDate);
            updatedDates[lesson.id] = ts;
            firestoreUpdates[`customLessonDates.${lesson.id}`] = ts;
        }

        setCustomDates(updatedDates); // optimistic
        setSaveError(null);
        try {
            await updateDoc(doc(db, 'userCatalogs', enrollmentId), firestoreUpdates);
        } catch (error) {
            console.error('Error saving your schedule:', error);
            setCustomDates(previousDates); // roll back — the date shown must match what's actually saved
            setSaveError('Your change couldn\u2019t be saved. Please try again.');
        }
    }, [enrollmentId, lessons, customDates, getEffectiveTargetDate]);

    const { weeks, currentWeekNumber, totalWeeks, behindCount, contentCompleteCount } = useMemo(() => {
        const enrolledDate = toDateObj(enrolledAt);
        const now = new Date();
        const weeksElapsed = Math.max(1, Math.floor((now - enrolledDate) / MS_PER_WEEK) + 1);
        const totalWeeksCalc = Math.ceil(lessons.length / LESSONS_PER_WEEK);
        const currentWeekCalc = Math.min(weeksElapsed, totalWeeksCalc);

        const lessonsWithStatus = lessons.map((lesson, index) => {
            const weekNumber = Math.ceil((index + 1) / LESSONS_PER_WEEK);
            const targetDate = getEffectiveTargetDate(lesson, index, customDates);
            const status = getLessonStatus(lesson, userProgress, tutorConfirmedLessonIds);
            return { lesson, weekNumber, targetDate, status };
        });

        const weeksMap = {};
        lessonsWithStatus.forEach((item) => {
            if (!weeksMap[item.weekNumber]) weeksMap[item.weekNumber] = [];
            weeksMap[item.weekNumber].push(item);
        });
        const weeksArray = Object.keys(weeksMap)
            .map(Number)
            .sort((a, b) => a - b)
            .map((weekNumber) => ({ weekNumber, items: weeksMap[weekNumber] }));

        const contentCompleteCountCalc = lessonsWithStatus.filter(
            (item) => item.status.contentComplete
        ).length;

        // Funded/cohort pace: fixed 2/week formula.
        // Individual pace: how many of the learner's own target dates have arrived.
        const expectedByNow = isIndividualLearner
            ? lessonsWithStatus.filter((item) => item.targetDate <= now).length
            : Math.min(lessons.length, currentWeekCalc * LESSONS_PER_WEEK);
        const behindCalc = Math.max(0, expectedByNow - contentCompleteCountCalc);

        return {
            weeks: weeksArray,
            currentWeekNumber: currentWeekCalc,
            totalWeeks: totalWeeksCalc,
            behindCount: behindCalc,
            contentCompleteCount: contentCompleteCountCalc,
        };
    }, [lessons, userProgress, enrolledAt, tutorConfirmedLessonIds, customDates, isIndividualLearner, getEffectiveTargetDate]);

    const visibleWeeks = expanded
        ? weeks
        : weeks.filter((w) =>
            w.weekNumber >= currentWeekNumber - WEEKS_SHOWN_AROUND_CURRENT &&
            w.weekNumber <= currentWeekNumber + WEEKS_SHOWN_AROUND_CURRENT
        );

    return (
        <div style={{
            backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e5e7eb',
            padding: '1rem 1.25rem', marginBottom: '1.5rem',
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <span style={{ fontWeight: 600, fontSize: '0.95rem', color: '#111827' }}>Your schedule</span>
                <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#6b7280' }}>
                    Week {currentWeekNumber} of {totalWeeks}
                </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '2px 0 8px' }}>
                {isIndividualLearner && (
                    <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>
                        This is your own plan — tap a date to adjust it.
                    </p>
                )}
                <button
                    onClick={() => setLegendOpen((open) => !open)}
                    style={{
                        marginLeft: 'auto', fontSize: '11px', padding: '2px 6px', whiteSpace: 'nowrap',
                        color: '#19a4a0', background: 'transparent', border: 'none', cursor: 'pointer',
                    }}
                >{legendOpen ? 'Hide' : 'What do these mean?'}</button>
            </div>

            {legendOpen && (
                <div style={{
                    display: 'flex', flexDirection: 'column', gap: '6px', padding: '10px',
                    margin: '0 0 10px', borderRadius: '8px', backgroundColor: '#f9fafb',
                }}>
                    {LESSON_STATE_LEGEND.map((item) => (
                        <div key={item.label} style={{ display: 'flex', gap: '8px', fontSize: '12px' }}>
                            <span style={{ color: item.color, width: '96px', flexShrink: 0 }}>{item.label}</span>
                            <span style={{ color: '#6b7280' }}>{item.text}</span>
                        </div>
                    ))}
                </div>
            )}

            {saveError && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px',
                    margin: '0 0 10px', borderRadius: '8px', backgroundColor: '#fee2e2',
                }}>
                    <span style={{ fontSize: '12px', color: '#991b1b' }}>{saveError}</span>
                </div>
            )}

            <div style={{
                display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px',
                margin: '8px 0 12px', borderRadius: '8px',
                backgroundColor: behindCount > 0 ? '#fef3c7' : '#f0fdfa',
            }}>
                <span style={{ fontSize: '12px', color: behindCount > 0 ? '#92400e' : '#0f766e' }}>
                    {contentCompleteCount} of {lessons.length} lessons done
                    {behindCount > 0
                        ? ` · ${behindCount} behind pace for your plan`
                        : ' · on track for your plan'}
                </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column' }}>
                {(() => {
                    let lastShownMonth = null; // only print a new month label when it actually changes
                    return visibleWeeks.map((week) => {
                        const isCurrent = week.weekNumber === currentWeekNumber;
                        const isPast = week.weekNumber < currentWeekNumber;
                        const bandColor = BAND_COLORS[(week.weekNumber - 1) % BAND_COLORS.length];
                        const monthLabel = formatMonthLabel(week.items[0].targetDate);
                        const showMonthLabel = monthLabel !== lastShownMonth;
                        lastShownMonth = monthLabel;

                        return (
                            <div
                                key={week.weekNumber}
                                style={{
                                    backgroundColor: bandColor,
                                    padding: '8px 10px',
                                    borderRadius: isCurrent ? '0 8px 8px 0' : '8px',
                                    borderLeft: isCurrent ? `3px solid ${CURRENT_WEEK_ACCENT}` : 'none',
                                }}
                            >
                                {showMonthLabel && (
                                    <span style={{
                                        fontSize: '11px',
                                        color: isCurrent ? CURRENT_WEEK_ACCENT : '#9ca3af',
                                        fontWeight: isCurrent ? 600 : 400,
                                    }}>
                                        {monthLabel}{isCurrent ? ' · current' : ''}
                                    </span>
                                )}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: showMonthLabel ? '4px' : 0 }}>
                                    {week.items.map(({ lesson, targetDate, status }) => (
                                        <LessonRow
                                            key={lesson.id}
                                            lesson={lesson}
                                            status={status}
                                            targetDate={targetDate}
                                            showProgressBar={isCurrent || isIndividualLearner}
                                            editable={isIndividualLearner && status.state !== 'done'}
                                            onDateChange={handleDateChange}
                                            isPast={isPast}
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    });
                })()}
            </div>

            {weeks.length > visibleWeeks.length && (
                <button
                    onClick={() => setExpanded(true)}
                    style={{
                        width: '100%', marginTop: '12px', padding: '8px', fontSize: '0.8rem',
                        border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: 'white',
                        color: '#19a4a0', cursor: 'pointer',
                    }}
                >View full schedule</button>
            )}
            {expanded && weeks.length > (WEEKS_SHOWN_AROUND_CURRENT * 2 + 1) && (
                <button
                    onClick={() => setExpanded(false)}
                    style={{
                        width: '100%', marginTop: '8px', padding: '8px', fontSize: '0.8rem',
                        border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: 'white',
                        color: '#6b7280', cursor: 'pointer',
                    }}
                >Show less</button>
            )}
        </div>
    );
};

export default React.memo(ScheduleTable);